import LifecycleSplitBar from '@app/components/lifecycle/lifecycle-split-bar';
import { useGetPackagingCCFDefaults } from '@shared/api/hooks/ccf/activities/packaging';
import {
  FoodActivityRetrieveUpdate,
  PackagingActivityRetrieve,
  PurchasedGoodModelledAsEnum,
} from '@shared/api/types';
import Text from '@shared/components/content/text';
import { renderUnit } from '@shared/components/content/unit';
import RatingCloud from '@shared/components/ratings/rating-cloud';
import { cn, formatSmallNumber } from '@shared/lib/utils';
import { ReactNode } from 'react';
import { DrillthroughLink } from './drillthrough-link';
import { ItemPanel } from './item-panel';

function Field({
  label,
  value,
  className,
}: {
  label: string;
  value: ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <Text variant="subtle">{label}</Text>
      <Text variant="body">{value ?? '-'}</Text>
    </div>
  );
}

const formatQuantity = (value: number) =>
  new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 }).format(value);

function modelledByLabel(item: FoodActivityRetrieveUpdate): string {
  switch (item.modelledAs) {
    case PurchasedGoodModelledAsEnum.basic_food:
      return item.purchasedGood.basicFood?.name ?? '-';
    case PurchasedGoodModelledAsEnum.modelled_food:
      return 'Custom recipe';
    case PurchasedGoodModelledAsEnum.external_emission_factor:
      return 'External product assessment';
    default:
      return '-';
  }
}

function packWeightLabel(
  itemsPerPack: number | undefined,
  itemMassOrVolume: number | undefined,
  unitSymbol: string | undefined
): string | null {
  if (itemMassOrVolume == null) return null;
  const amount =
    `${formatQuantity(itemMassOrVolume)} ${unitSymbol ?? ''}`.trim();
  return itemsPerPack != null && itemsPerPack !== 1
    ? `${formatQuantity(itemsPerPack)} × ${amount}`
    : amount;
}

function IntensityValue({
  emissionFactor,
  rating,
  unit = 'kgCO2e/kg',
  className,
}: {
  emissionFactor: number;
  rating?: FoodActivityRetrieveUpdate['emissionsCategory'];
  unit?: 'kgCO2e/kg' | 'kgCO2e';
  className?: string;
}) {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      {rating ? <RatingCloud rating={rating} className="h-7 w-auto" /> : null}
      <span className="flex items-baseline gap-x-2">
        <Text className="text-3xl font-medium leading-none text-primary-800">
          {formatSmallNumber(emissionFactor)}
        </Text>
        <Text className="text-sm font-medium text-primary-500">
          {renderUnit(unit)}
        </Text>
      </span>
    </div>
  );
}

type FoodProps = {
  item: FoodActivityRetrieveUpdate;
  /** Link target for the Edit modelling drill-through (the Modelling tab). */
  modellingHref: string;
};

/**
 * Read-only summary of how a food and drink purchased good is modelled:
 * identifiers, the matched food or recipe, pack weight, carbon intensity and
 * the lifecycle-stage split of its emission factor.
 */
export function FoodModellingCard({ item, modellingHref }: FoodProps) {
  const factors = item.emissionsFactors;
  // Original unit and pack weight describe how a pack is configured; an item
  // whose purchases are imported as total weight has neither. inputMode is
  // null on older imports, so pack mode is inferred from the pack fields.
  const isPackMode = item.itemMassOrVolume != null || item.itemsPerPack != null;
  return (
    <ItemPanel
      title="Modelling"
      headerAction={
        <DrillthroughLink to={modellingHref}>Edit modelling</DrillthroughLink>
      }
      contentClassName="space-y-4"
    >
      <div>
        <div className="flex items-baseline justify-between gap-4">
          <Text variant="subtle">{item.remoteId}</Text>
          {isPackMode ? <Text variant="subtle">Original unit</Text> : null}
        </div>
        <div className="flex items-baseline justify-between gap-4">
          <Text variant="body">{item.purchasedGood.name}</Text>
          {isPackMode ? (
            <Text variant="body" className="shrink-0">
              {item.inputUnit || '-'}
            </Text>
          ) : null}
        </div>
      </div>
      <div className="space-y-4 border-t border-gray-100 pt-4">
        <div className="flex items-start justify-between gap-4">
          <Field label="Modelled by" value={modelledByLabel(item)} />
          {isPackMode ? (
            <Field
              label="Pack weight"
              value={packWeightLabel(
                item.itemsPerPack,
                item.itemMassOrVolume,
                item.itemUnit?.symbol
              )}
              className="shrink-0 text-right"
            />
          ) : null}
        </div>
        <div className="grid gap-x-8 gap-y-4 sm:grid-cols-[auto_1fr]">
          {item.emissionFactor != null ? (
            <div className="min-w-36 space-y-2">
              <Text variant="subtle">Carbon intensity</Text>
              <IntensityValue
                emissionFactor={item.emissionFactor}
                rating={item.emissionsCategory}
              />
            </div>
          ) : null}
          <div className="space-y-2">
            <Text variant="subtle">Emissions breakdown</Text>
            <LifecycleSplitBar
              farming={factors?.farmingEmissionsPerKg ?? null}
              processing={factors?.processingEmissionsPerKg ?? null}
              packaging={factors?.packagingEmissionsPerKg ?? null}
              transport={factors?.transportEmissionsPerKg ?? null}
            />
          </div>
        </div>
      </div>
    </ItemPanel>
  );
}

type PackagingProps = {
  item: PackagingActivityRetrieve;
  modellingHref: string;
};

/**
 * Read-only summary of how a packaging purchased good is modelled. Packaging
 * has no carbon rating or lifecycle-stage breakdown, so the card is limited
 * to identifiers, the packaging type and the carbon intensity.
 */
export function PackagingModellingCard({
  item,
  modellingHref,
}: PackagingProps) {
  const { data: packagingGroups } = useGetPackagingCCFDefaults();
  // The size only disambiguates when the packaging type comes in several
  // sizes; single-size types read cleaner as just the type name.
  const hasMultipleSizes =
    (packagingGroups?.find((group) => group.name === item.packagingType?.name)
      ?.sizes.length ?? 0) > 1;
  const modelledBy = item.packagingType
    ? [
        item.packagingType.name,
        hasMultipleSizes ? item.packagingSize?.description : null,
      ]
        .filter(Boolean)
        .join(' - ')
    : '-';
  return (
    <ItemPanel
      title="Modelling"
      headerAction={
        <DrillthroughLink to={modellingHref}>Edit modelling</DrillthroughLink>
      }
      contentClassName="space-y-4"
    >
      <div>
        <div className="flex items-baseline justify-between gap-4">
          <Text variant="subtle">{item.remoteId}</Text>
          <Text variant="subtle">Original unit</Text>
        </div>
        <div className="flex items-baseline justify-between gap-4">
          <Text variant="body">{item.packagingName}</Text>
          <Text variant="body" className="shrink-0">
            {item.inputUnit || '-'}
          </Text>
        </div>
      </div>
      <div className="space-y-4 border-t border-gray-100 pt-4">
        <div className="flex items-start justify-between gap-4">
          <Field label="Modelled by" value={modelledBy} />
          <Field
            label="Items per pack"
            value={
              item.itemsPerPack != null
                ? formatQuantity(item.itemsPerPack)
                : '-'
            }
            className="shrink-0 text-right"
          />
        </div>
        {item.emissionFactor != null ? (
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2">
              <Text variant="subtle">Emissions per item</Text>
              <IntensityValue
                emissionFactor={item.emissionFactor}
                unit="kgCO2e"
              />
            </div>
            {item.itemsPerPack != null && item.itemsPerPack > 1 ? (
              <div className="space-y-2 text-right">
                <Text variant="subtle">Emissions per pack</Text>
                <IntensityValue
                  emissionFactor={item.emissionFactor * item.itemsPerPack}
                  unit="kgCO2e"
                  className="justify-end"
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </ItemPanel>
  );
}
